import React from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronRight, User, LogOut } from 'lucide-react';
import { cognitoAuth } from '@/services/cognitoAuth';

interface AdminHeaderProps {
  sidebarCollapsed?: boolean;
}

const getPageTitle = (pathname: string): string => {
  if (pathname === '/admin') return 'Dashboard';
  if (pathname.startsWith('/admin/batches/new')) return 'Create Batch';
  if (pathname.startsWith('/admin/batches/')) return 'Batch Details';
  if (pathname.startsWith('/admin/batches')) return 'Batch Management';
  if (pathname.startsWith('/admin/search')) return 'Individual Search';
  if (pathname.startsWith('/admin/users')) return 'User Management';
  if (pathname.startsWith('/admin/settings')) return 'Settings';
  return 'Admin Portal';
};

const getBreadcrumbs = (pathname: string): Array<{ label: string; href?: string }> => {
  const segments = pathname.split('/').filter(Boolean);
  const breadcrumbs: Array<{ label: string; href?: string }> = [{ label: 'Admin', href: '/admin' }];
  
  if (segments.length > 1) {
    const section = segments[1];
    if (section === 'batches') {
      breadcrumbs.push({ label: 'Batches', href: '/admin/batches' });
      if (segments[2] === 'new') {
        breadcrumbs.push({ label: 'New Batch' });
      } else if (segments[2]) {
        breadcrumbs.push({ label: `Batch ${segments[2]}` });
      }
    } else {
      const sectionLabels: Record<string, string> = {
        search: 'Search',
        users: 'Users', 
        settings: 'Settings'
      };
      breadcrumbs.push({ label: sectionLabels[section] || section });
    }
  }
  
  return breadcrumbs;
};

export const AdminHeader: React.FC<AdminHeaderProps> = () => {
  const location = useLocation();
  const pageTitle = getPageTitle(location.pathname);
  const breadcrumbs = getBreadcrumbs(location.pathname);
  
  const currentUser = cognitoAuth.getCurrentUser();

  const handleLogout = async () => {
    await cognitoAuth.signOut();
    // Refresh the page to show login form
    window.location.reload();
  };

  return (
    <header className="bg-white border-b border-slate-200 px-6 py-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">{pageTitle}</h1>
          <nav className="flex items-center space-x-1 text-sm text-slate-500 mt-1">
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={crumb.label}>
                {index > 0 && (
                  <ChevronRight className="h-3 w-3 mx-1" />
                )}
                <span className={index === breadcrumbs.length - 1 ? "text-slate-900" : "hover:text-slate-700"}>
                  {crumb.label}
                </span>
              </React.Fragment>
            ))}
          </nav>
        </div>
        
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2 text-sm text-slate-600">
            <User className="h-4 w-4" />
            <div className="text-right">
              <div className="font-medium">{currentUser?.name || 'Admin User'}</div>
              <div className="text-xs text-slate-500">{currentUser?.companyName || 'Unknown Company'}</div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center space-x-1 text-sm text-slate-600 hover:text-slate-900 px-2 py-1 rounded-lg hover:bg-slate-100 transition-colors"
            title="Sign Out"
          >
            <LogOut className="h-4 w-4" />
            <span>Sign Out</span>
          </button>
        </div>
      </div>
    </header>
  );
};